<template>
  <!-- begin:: Footer -->
  <div class="footer d-flex flex-lg-column position-relative">
    <button
      class="btn position-absolute bg-gray-300 text-dark-50 py-1 border-gray border-bottom-0 rounded-bottom-0"
      style="top: -25px;left: 25px;"
      @click="toggleTerminal"
    >
      terminal
    </button>
    <div
      dir="ltr"
      class="text-right bg-gray-300 py-3 border-1 border-gray border-top w-100"
    >
      <perfect-scrollbar
        class="scroll bg-gray-900 m-4 p-0"
        id="scroller"
        style="max-height: 30vh; position: relative"
        v-show="show"
      >
        <div
          id="terminal_content"
          ref="content"
          class="w-100 m-0 p-3 font-family-tahoma min-h-100px text-white"
          dir="ltr"
          @click="focusInput"
        >
          <div v-for="(line, i) in terminalLines" :key="line.time + i">
            <span>
              <span class="text-white ml-1">[{{ line.time }}]</span>
              <span
                :class="[
                  line.type === 1 && 'text-success',
                  line.type === 2 && 'text-danger',
                  line.type === 3 && 'text-warning'
                ]"
              >
                {{ line.message }}
              </span>
            </span>
          </div>
          <div class="d-flex">
            <span class="pl-2"><i class="far fa-angle-right"></i></span>
            <span
              v-if="terminalDevice.code"
              class="text-light-primary font-size-xs pl-2 align-self-center"
            >
              {{ terminalDevice.code }}
            </span>
            <input
              type="text"
              id="terminal_input"
              ref="terminal_input"
              @keyup.enter="submitInput"
            />
          </div>
        </div>
      </perfect-scrollbar>
    </div>
  </div>
  <!-- end:: Footer -->
</template>

<style>
#terminal_input {
  width: 100%;
  background-color: #181c32;
  color: white;
  border: none;
  border-radius: 0;
}

#terminal_input:focus {
  outline: none !important;
}

#terminal_input:focus-visible {
  outline: none !important;
}
</style>
<script>
import mqtt from "mqtt";
import {
  SET_TERMINAL_CONTENT,
  SET_TERMINAL_DEVICE,
  TERMINAL_ADD_LINE
} from "@/core/services/store/terminal.module";
import { FETCH_ACLS, SYNC_ACLS } from "@/core/services/store/acls.module";
import { FETCH_DEVICES } from "@/core/services/store/devices.module";
import { mapGetters } from "vuex";

export default {
  name: "Terminal",
  data() {
    return {
      try: false,
      show: false,
      clients: {},
      connection: {
        host: process.env.VUE_APP_MQTT_URL,
        endpoint: "/mqtt",
        clean: true, // Reserved session
        connectTimeout: 4000, // Time out
        reconnectPeriod: 4000, // Reconnection interval
        // Certification Information
        clientId: "web_",
        username: "",
        password: ""
      }
    };
  },
  computed: {
    ...mapGetters([
      "terminalLines",
      "currentUserPersonalInfo",
      "mqttUsers",
      "devices",
      "terminalDevice"
    ])
  },
  mounted() {
    this.$store.dispatch(SET_TERMINAL_CONTENT, []);
    this.getAcls()
      .then(this.getDevice)
      .then(data => {
        this.createConnection(data);
      });

    this.eventHub.$on("set_device_status", this.sendQuery);
  },
  methods: {
    getAcls() {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch(FETCH_ACLS)
          .then(data => {
            if (!data.data.length && !this.try) {
              this.try = true;
              this.$store.dispatch(SYNC_ACLS).then(() => {
                this.getAcls();
              });
            }
            resolve(data);
          })
          .catch(() => {
            reject();
          });
      });
    },
    getDevice() {
      return new Promise(resolve => {
        this.loaded = false;

        this.$store
          .dispatch(FETCH_DEVICES)
          .then(() => {
            this.loaded = true;
            resolve();
          })
          .catch(() => {
            this.loaded = true;
          });
      });
    },
    createConnection() {
      const { host, endpoint, ...options } = this.connection;
      const connectUrl = host + endpoint;

      this.mqttUsers?.forEach(user => {
        if (user["username"]) {
          options.clientId = `web_${user["id"]}_${new Date().getTime()}`;
          options.username = user["username"];
          options.password = user["password_raw"];
        } else {
          return;
        }

        try {
          this.clients[user.id] = mqtt.connect(connectUrl, options);
        } catch (error) {
          this.$store.dispatch(TERMINAL_ADD_LINE, {
            message: "Mqtt Connection Error",
            user: user.id,
            type: 2
          });
        }
        this.clients[user.id].on("connect", () => {
          this.$store.dispatch(TERMINAL_ADD_LINE, {
            message: `Connection succeeded! { username: ${user["username"]}, password: ${user["password_raw"]} }`,
            user: user.code,
            type: 1
          });

          this.devices?.forEach(device => {
            this.clients[user.id].subscribe(
              "query/server/" + device["code"],
              err => {
                if (!err) {
                  this.$store.dispatch(TERMINAL_ADD_LINE, {
                    message: `Listening to channel #query/server/${device["code"]}`,
                    user: user.id,
                    type: 1
                  });
                }
              }
            );
            this.clients[user.id].subscribe(
              "query/client/" + device["code"],
              err => {
                if (!err) {
                  this.$store.dispatch(TERMINAL_ADD_LINE, {
                    message: `Listening to channel #query/client/${device["code"]}`,
                    user: user.id,
                    type: 1
                  });
                }
              }
            );
          });
        });
        this.clients[user.id].on("error", error => {
          this.$store.dispatch(TERMINAL_ADD_LINE, {
            message: `Connection failed: ${error.toString()}`,
            user: user.id,
            type: 2
          });
        });
        this.clients[user.id].on("message", (topic, message) => {
          let txt = "";
          if (topic.startsWith("query/server")) {
            txt = `Received message "${message}"`;
          } else if (topic.startsWith("query/client")) {
            txt = `send message "${message}"`;
          }

          this.$store.dispatch(TERMINAL_ADD_LINE, {
            message: txt,
            user: user.id,
            type: 1
          });

          let objDiv = document.getElementById("scroller");
          objDiv.scrollTop = objDiv.scrollHeight + 100;

          let m = new TextDecoder().decode(message);
          let parts = m.split(" ");

          if (topic.startsWith("query/server") && parts[0] === "SET") {
            this.eventHub.$emit(`update_device_${parts[1]}`, parts[3]);
          }
        });
      });
    },
    toggleTerminal() {
      this.show = !this.show;
    },
    focusInput() {
      document.getElementById("terminal_input").focus();
    },
    sendQuery(payload) {
      if (Object.values(this.clients)[0]) {
        Object.values(this.clients)[0].publish(
          `query/client/${payload.code}`,
          payload.query
        );
      }
    },
    submitInput() {
      let value = this.$refs["terminal_input"].value;

      if (value.startsWith("device")) {
        let activeDeviceKey = value.split(" ")[1];
        let activeDevice = this.devices.filter(p => {
          return p.code === activeDeviceKey;
        });

        if (activeDevice[0]) {
          this.$store.dispatch(SET_TERMINAL_DEVICE, activeDevice[0]);
        }
      } else if (value.startsWith("query")) {
        let query = value.replace("query ", "").replaceAll('"', "");

        if (this.terminalDevice) {
          this.sendQuery({
            code: this.terminalDevice.code,
            query: query
          });
        } else {
          this.$store.dispatch(TERMINAL_ADD_LINE, {
            message: `Active user not found, first set it (Enter: user user_id)`,
            type: 3
          });
          return;
        }
      }

      this.$store.dispatch(TERMINAL_ADD_LINE, {
        message: value,
        type: 4
      });
      this.$refs["terminal_input"].value = "";
    }
  }
};
</script>
<style lang="scss">
.border-gray {
  border-color: #dedfe5;
}
</style>
